import {CommonModule} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {Router, RouterModule} from '@angular/router';
import {ApiMode, ConfigService} from '@shared-library';
import {BROWSERS, DeviceDetectorService, OS} from 'ngx-device-detector';
import {AuthService, VaultAuthSessionDebugInfo} from '../../services/auth.service';
import {FdkButtonComponent, FdkIllustrationComponent, FdkLinkComponent} from 'fasten-design-kit';

interface BrowserCookieDocumentationLink {
  browserName: string;
  url: string;
}

const GENERIC_DOCUMENTATION = 'generic';

type BrowserCookieDocumentationMatrix = Readonly<
  Record<string, Readonly<Record<string, BrowserCookieDocumentationLink>>>
>;

const EDGE_COOKIE_DOCUMENTATION: Readonly<Record<string, BrowserCookieDocumentationLink>> = {
  [GENERIC_DOCUMENTATION]: {
    browserName: 'Microsoft Edge',
    url: 'https://support.microsoft.com/en-us/edge/manage-cookies-in-microsoft-edge-view-allow-block-delete-and-use',
  },
};

const THIRD_PARTY_COOKIE_DOCUMENTATION_MATRIX: BrowserCookieDocumentationMatrix = {
  [BROWSERS.CHROME]: {
    [GENERIC_DOCUMENTATION]: {
      browserName: 'Google Chrome',
      url: 'https://support.google.com/chrome/answer/95647',
    },
  },
  [BROWSERS.FIREFOX]: {
    [GENERIC_DOCUMENTATION]: {
      browserName: 'Firefox',
      url: 'https://support.mozilla.org/en-US/kb/third-party-cookies-firefox-tracking-protection',
    },
    [OS.ANDROID]: {
      browserName: 'Firefox',
      url: 'https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-android',
    },
    [OS.IOS]: {
      browserName: 'Firefox',
      url: 'https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-ios',
    },
  },
  [BROWSERS.SAFARI]: {
    [GENERIC_DOCUMENTATION]: {
      browserName: 'Safari',
      url: 'https://support.apple.com/guide/safari/enable-cookies-ibrw850f6c51/mac',
    },
    [OS.IOS]: {
      browserName: 'Safari',
      url: 'https://support.apple.com/guide/iphone/iphb01fc3c85/ios',
    },
  },
  [BROWSERS.OPERA]: {
    [GENERIC_DOCUMENTATION]: {
      browserName: 'Opera',
      url: 'https://help.opera.com/en/latest/web-preferences/#cookies',
    },
  },
  [BROWSERS.MS_EDGE]: EDGE_COOKIE_DOCUMENTATION,
  [BROWSERS.MS_EDGE_CHROMIUM]: EDGE_COOKIE_DOCUMENTATION,
};

@Component({
  selector: 'app-third-party-cookies-error',
  templateUrl: './third-party-cookies-error.component.html',
  styleUrl: './third-party-cookies-error.component.css',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FdkIllustrationComponent,
    FdkLinkComponent,
    FdkButtonComponent,
  ],
})
export class ThirdPartyCookiesErrorComponent implements OnInit {
  cookieDebugInfo?: VaultAuthSessionDebugInfo;
  showCookieDebugInfo = false;
  thirdPartyCookieDocumentationLink?: BrowserCookieDocumentationLink;
  requiresSafariOnIos = false;
  safariDeepLink?: string;

  constructor(
    private authService: AuthService,
    private configService: ConfigService,
    private deviceDetectorService: DeviceDetectorService,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    this.requiresSafariOnIos = this.isUnsupportedIosBrowser();
    if (this.requiresSafariOnIos) {
      // Reopen the host page when embedded so its original Fasten configuration is preserved.
      this.safariDeepLink = this.getSafariDeepLink(document.referrer || window.location.href);
    } else {
      this.thirdPartyCookieDocumentationLink = this.getThirdPartyCookieDocumentationLink();
    }

    this.showCookieDebugInfo = this.isCookieDebugEnabled();
    if (this.showCookieDebugInfo) {
      this.cookieDebugInfo = await this.authService.GetVaultAuthSessionDebugInfo();
    }
  }

  isUnsupportedIosBrowser(): boolean {
    return this.deviceDetectorService.os === OS.IOS
      && this.deviceDetectorService.browser !== BROWSERS.SAFARI;
  }

  getSafariDeepLink(targetUrl: string): string | undefined {
    try {
      const url = new URL(targetUrl);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return undefined;
      }

      return `x-safari-${url.href}`;
    } catch {
      return undefined;
    }
  }

  getThirdPartyCookieDocumentationLink(): BrowserCookieDocumentationLink | undefined {
    const browserDocumentation = THIRD_PARTY_COOKIE_DOCUMENTATION_MATRIX[this.deviceDetectorService.browser];

    return browserDocumentation?.[this.deviceDetectorService.os]
      ?? browserDocumentation?.[GENERIC_DOCUMENTATION];
  }

  backToSignIn(): void {
    void this.router.navigate(['/auth/signin']);
  }

  private isCookieDebugEnabled(): boolean {
    const systemConfig = this.configService.systemConfig$;

    return systemConfig.apiMode === ApiMode.Test
      && systemConfig.showCookieDebugInfo === true;
  }
}
